import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { cn } from '@/lib/utils'
import type { CapacityDay } from '@/api/resourceCenterApi'
import { dayHeader, hoursLabel } from '@/lib/resourceCalendar'
import { WORK_HEX } from './shared'

// Three more ways to read the same capacity rows as the calendar (Overview tab):
// a stacked chart over time, a heatmap of available hours, and a plain table.
// All take the window's days, so they always agree with the calendar.

/** The work types in the order every view stacks and lists them. */
const SERIES = [
  { key: 'prHrs', label: 'Process', color: WORK_HEX.PROCESS },
  { key: 'rvHrs', label: 'Review', color: WORK_HEX.REVIEW },
  { key: 'qaHrs', label: 'QA', color: WORK_HEX.QA },
  { key: 'saHrs', label: 'Support', color: WORK_HEX.SUPPORT },
  { key: 'flexHrs', label: 'Dynamic', color: WORK_HEX.FLEX },
] as const

/** Hours left for the work after absences. */
const available = (d: CapacityDay) => Math.max(0, d.shiftHrs - d.uaHrs)

function stateLabel(d: CapacityDay): string {
  if (d.state === 'OFF') return 'Off'
  if (d.state === 'NOT_STARTED') return 'Not started'
  if (d.state === 'UNAVAILABLE') return d.uaCode ? `Absent (${d.uaCode})` : 'Absent'
  return d.shiftCode ?? 'On'
}

// ── Chart ────────────────────────────────────────────────────────────────────

/**
 * Hours per day stacked by work type, with the shift's length as a dashed line
 * on the same hours axis: where the bars stop short of the line, an absence
 * took the difference.
 */
export function CapacityChart({ days, today }: { days: CapacityDay[]; today: string }) {
  const data = days.map(d => {
    const h = dayHeader(d.date)
    return { ...d, label: `${h.dow.slice(0, 2)} ${h.md}`, shift: d.state === 'NOT_STARTED' ? null : d.shiftHrs }
  })
  const used = SERIES.filter(s => days.some(d => d[s.key] > 0))
  const worked = days.filter(d => d.state === 'ON' || d.state === 'UNAVAILABLE')
  const average = worked.length ? worked.reduce((n, d) => n + available(d), 0) / worked.length : 0
  const todayLabel = data.find(d => d.date === today)?.label
  return (
    <div className='space-y-3'>
      <div className='h-[26rem] w-full rounded-xl border bg-gradient-to-b from-muted/30 to-background p-3'>
        <ResponsiveContainer width='100%' height='100%'>
          <ComposedChart data={data} margin={{ top: 20, right: 12, bottom: 4, left: -12 }} barCategoryGap='18%'>
            <defs>
              {/* One hue per work type, a little lighter at the top. */}
              {SERIES.map(s => (
                <linearGradient key={s.key} id={`cap-${s.key}`} x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='0%' stopColor={s.color} stopOpacity={0.78} />
                  <stop offset='100%' stopColor={s.color} stopOpacity={1} />
                </linearGradient>
              ))}
            </defs>
            {todayLabel && (
              <ReferenceArea x1={todayLabel} x2={todayLabel} fill='rgb(251 191 36)' fillOpacity={0.14} ifOverflow='extendDomain' />
            )}
            <CartesianGrid vertical={false} stroke='hsl(var(--border))' strokeDasharray='3 3' />
            <XAxis
              dataKey='label'
              tickLine={false}
              axisLine={false}
              interval={days.length > 16 ? 'preserveStartEnd' : 0}
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              unit=' h'
            />
            <Tooltip
              cursor={{ fill: 'hsl(var(--muted))', opacity: 0.5 }}
              content={({ active, payload }) => {
                const d = active ? (payload?.[0]?.payload as (CapacityDay & { label: string }) | undefined) : undefined
                return d ? <DayTooltip day={d} isToday={d.date === today} /> : null
              }}
            />
            {used.map((s, i) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.label}
                stackId='hours'
                fill={`url(#cap-${s.key})`}
                stroke='hsl(var(--background))'
                strokeWidth={2}
                radius={i === used.length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]}
                maxBarSize={40}
                animationDuration={500}
              />
            ))}
            {average > 0 && (
              <ReferenceLine
                y={average}
                stroke='hsl(var(--foreground))'
                strokeOpacity={0.35}
                strokeDasharray='2 4'
                label={{
                  value: `avg ${hoursLabel(average)} h`,
                  position: 'insideTopRight',
                  fontSize: 10,
                  fill: 'hsl(var(--muted-foreground))',
                }}
              />
            )}
            <Line
              dataKey='shift'
              name='Shift length'
              type='stepAfter'
              stroke='hsl(var(--muted-foreground))'
              strokeWidth={2}
              strokeDasharray='4 3'
              dot={false}
              connectNulls={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className='flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground'>
        {used.map(s => (
          <span key={s.key} className='flex items-center gap-1.5'>
            <span className='h-2.5 w-2.5 rounded-sm' style={{ background: s.color }} /> {s.label}
          </span>
        ))}
        <span className='flex items-center gap-1.5'>
          <span className='w-4 border-t-2 border-dashed border-muted-foreground' /> Shift length
        </span>
      </div>
    </div>
  )
}

function DayTooltip({ day, isToday }: { day: CapacityDay; isToday: boolean }) {
  const h = dayHeader(day.date)
  return (
    <div className='min-w-[11rem] rounded-lg border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md'>
      <p className='mb-1 font-semibold'>
        {h.dow} {h.md}
        {isToday && <span className='ml-1.5 rounded bg-amber-100 px-1 text-[10px] text-amber-800'>Today</span>}
        <span className='ml-1.5 font-normal text-muted-foreground'>{stateLabel(day)}</span>
      </p>
      {day.state !== 'NOT_STARTED' && day.state !== 'OFF' && (
        <>
          <TipRow label='Available' value={`${hoursLabel(available(day))} h`} bold />
          {day.uaHrs > 0 && <TipRow label={`Absence${day.uaCode ? ` ${day.uaCode}` : ''}`} value={`−${hoursLabel(day.uaHrs)} h`} />}
          {SERIES.filter(s => day[s.key] > 0).map(s => (
            <TipRow key={s.key} label={s.label} value={`${hoursLabel(day[s.key])} h`} color={s.color} />
          ))}
        </>
      )}
    </div>
  )
}

function TipRow({ label, value, color, bold }: { label: string; value: string; color?: string; bold?: boolean }) {
  return (
    <div className='flex items-center justify-between gap-4 py-0.5'>
      <span className='flex items-center gap-1.5 text-muted-foreground'>
        {color && <span className='h-2 w-2 rounded-sm' style={{ background: color }} />}
        {label}
      </span>
      <span className={cn('tabular-nums', bold && 'font-semibold text-foreground')}>{value}</span>
    </div>
  )
}

// ── Heatmap ──────────────────────────────────────────────────────────────────

/**
 * Available hours as one blue, light to dark, laid out Sun → Sat like the
 * calendar: busy and empty stretches show at a glance. Off and not-started
 * days are blank; an absent day is striped.
 */
export function CapacityHeatmap({
  dates,
  byDate,
  today,
  leading,
  stripes,
}: {
  dates: string[]
  byDate: Map<string, CapacityDay>
  today: string
  /** Blank cells before the first date, so weekdays line up. */
  leading: number
  stripes: string
}) {
  const max = Math.max(1, ...dates.map(d => (byDate.get(d) ? available(byDate.get(d)!) : 0)))
  return (
    <div className='space-y-2'>
      <div className='grid grid-cols-7 gap-1 text-center text-[10px] font-medium uppercase tracking-wide text-muted-foreground'>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <span key={d}>{d}</span>
        ))}
      </div>
      <div className='grid grid-cols-7 gap-1'>
        {Array.from({ length: leading }, (_, i) => (
          <span key={`pad-${i}`} />
        ))}
        {dates.map(date => {
          const d = byDate.get(date)
          const h = dayHeader(date)
          const hrs = d ? available(d) : 0
          const on = d && d.state !== 'OFF' && d.state !== 'NOT_STARTED'
          const absent = d && (d.state === 'UNAVAILABLE' || (d.uaHrs > 0 && hrs === 0))
          // 0.12 → 1: an ON day with any hours is always visible.
          const alpha = on && hrs > 0 ? 0.12 + 0.88 * (hrs / max) : 0
          return (
            <div
              key={date}
              title={d ? `${h.dow} ${h.md} · ${stateLabel(d)} · ${hoursLabel(hrs)} h available` : `${h.dow} ${h.md}`}
              className={cn(
                'flex h-20 flex-col items-center justify-center rounded-lg border text-xs leading-tight transition-transform hover:scale-[1.03]',
                date === today && 'ring-2 ring-amber-400 ring-offset-1',
                !on && !absent && 'bg-muted/50 text-muted-foreground',
              )}
              style={
                absent
                  ? { backgroundImage: stripes }
                  : on
                    ? { backgroundColor: `rgb(37 99 235 / ${alpha})`, color: alpha > 0.55 ? 'white' : undefined }
                    : undefined
              }
            >
              <span className='opacity-70'>{h.md}</span>
              <span className='font-semibold tabular-nums'>{on ? hoursLabel(hrs) : absent ? d?.uaCode ?? 'UA' : '—'}</span>
            </div>
          )
        })}
      </div>
      <div className='flex items-center gap-2 text-xs text-muted-foreground'>
        <span>0 h</span>
        <span
          className='h-2 w-32 rounded-full'
          style={{ background: 'linear-gradient(90deg, rgb(37 99 235 / 0.12), rgb(37 99 235 / 1))' }}
        />
        <span>{hoursLabel(max)} h available</span>
      </div>
    </div>
  )
}

// ── Table ────────────────────────────────────────────────────────────────────

/** Every day as a row: the numbers behind the other views, for reading exactly. */
export function CapacityTable({ days, today }: { days: CapacityDay[]; today: string }) {
  const total = (pick: (d: CapacityDay) => number) => days.reduce((n, d) => n + pick(d), 0)
  return (
    <div className='overflow-x-persistent rounded-lg border'>
      <table className='w-full text-sm'>
        <thead className='bg-muted/50 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>
          <tr>
            <th className='px-3 py-2 text-left'>Day</th>
            <th className='px-3 py-2 text-left'>Shift</th>
            <th className='px-3 py-2 text-right'>Shift h</th>
            <th className='px-3 py-2 text-right'>Absence</th>
            {SERIES.map(s => (
              <th key={s.key} className='px-3 py-2 text-right'>
                <span className='inline-flex items-center gap-1'>
                  <span className='h-2 w-2 rounded-sm' style={{ background: s.color }} />
                  {s.label}
                </span>
              </th>
            ))}
            <th className='px-3 py-2 text-right'>Available</th>
          </tr>
        </thead>
        <tbody className='divide-y'>
          {days.map(d => {
            const h = dayHeader(d.date)
            const quiet = d.state === 'OFF' || d.state === 'NOT_STARTED'
            return (
              <tr
                key={d.date}
                className={cn(
                  'tabular-nums',
                  h.weekend && 'bg-muted/20',
                  d.date === today && 'bg-amber-50',
                  quiet && 'text-muted-foreground',
                )}
              >
                <td className='whitespace-nowrap px-3 py-1.5 font-medium'>
                  {h.dow} {h.md}
                </td>
                <td className='px-3 py-1.5'>{stateLabel(d)}</td>
                <td className='px-3 py-1.5 text-right'>{quiet ? '—' : hoursLabel(d.shiftHrs)}</td>
                <td className='px-3 py-1.5 text-right'>{d.uaHrs > 0 ? `${hoursLabel(d.uaHrs)}${d.uaCode ? ` ${d.uaCode}` : ''}` : '—'}</td>
                {SERIES.map(s => (
                  <td key={s.key} className='px-3 py-1.5 text-right'>
                    {d[s.key] > 0 ? hoursLabel(d[s.key]) : '—'}
                  </td>
                ))}
                <td className='px-3 py-1.5 text-right font-semibold'>{quiet ? '—' : hoursLabel(available(d))}</td>
              </tr>
            )
          })}
        </tbody>
        <tfoot className='border-t-2 bg-muted/30 font-semibold tabular-nums'>
          <tr>
            <td className='px-3 py-2' colSpan={2}>
              Total
            </td>
            <td className='px-3 py-2 text-right'>{hoursLabel(total(d => (d.state === 'ON' || d.state === 'UNAVAILABLE' ? d.shiftHrs : 0)))}</td>
            <td className='px-3 py-2 text-right'>{hoursLabel(total(d => d.uaHrs))}</td>
            {SERIES.map(s => (
              <td key={s.key} className='px-3 py-2 text-right'>
                {hoursLabel(total(d => d[s.key]))}
              </td>
            ))}
            <td className='px-3 py-2 text-right'>{hoursLabel(total(available))}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}
